import { useEffect, useState } from 'react';
import { Link, Outlet } from '@tanstack/react-router';
import { allowed } from '@buildmate/core';
import { Button } from '@buildmate/ui-web';
import { SETTINGS_MODULES } from './SettingsLayout.tsx';
import { usePorts } from '../lib/ports-context.tsx';
import { useSession } from '../lib/session-context.tsx';
import { CompanySwitcher } from './CompanySwitcher.tsx';
import { AssistantPane } from './AssistantPane.tsx';
import { NotificationBell } from './NotificationBell.tsx';
import { JobSidebar } from './JobSidebar.tsx';
import { SafetySplash } from './SafetySplash.tsx';
import { NewJobDialog } from './NewJobDialog.tsx';

/** Still to come. `Financial` left the list when its menu became real. */
const NAV = ['Project Management', 'Files', 'Messaging'];

export function AppLayout() {
  const ports = usePorts();
  const { user, membership, company, principal } = useSession();
  const [creating, setCreating] = useState(false);
  const [asking, setAsking] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  /**
   * Escape closes the menu wherever the focus happens to be.
   *
   * It was a handler on the panel first, which is wrong for the
   * ordinary case: after clicking the button, focus is on the
   * **button**, so the key never reached the panel and the menu stayed
   * open. A document listener while open is the behaviour people
   * already expect from a menu.
   */
  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [menuOpen]);

  /**
   * The main navigation, as data (SCRUM-299).
   *
   * One list rather than two copies of the same links, because below
   * `lg` the whole bar used to be `hidden` and a phone was left with a
   * single Jobs link — no People, no Inventory, no Settings. The
   * people most likely to open this on a phone are the ones standing
   * on the job, so "the navigation is desktop only" is the wrong way
   * round.
   *
   * Two renderings of one array: a row on a wide screen, a panel
   * behind a button on a narrow one. Two hand-written copies would
   * drift the first time a module was added — which is exactly how the
   * bar came to have links the menu would not have had.
   */
  const links = [
    { to: '/app', label: 'Jobs' },
    { to: '/app/people', label: 'People' },
    { to: '/app/directory', label: 'Directory' },
    ...(principal && allowed(principal, 'equipment', 'view')
      ? [{ to: '/app/settings/equipment', label: 'Equipment', search: { view: 'where' as const } }]
      : []),
    ...(principal && allowed(principal, 'catalog', 'view')
      ? [{ to: '/app/inventory', label: 'Inventory' }]
      : []),
    ...(principal && allowed(principal, 'timeClock', 'edit')
      ? [{ to: '/app/time', label: 'Time' }]
      : []),
    ...(principal && allowed(principal, 'reports', 'view')
      ? [{ to: '/app/reports', label: 'Reports' }]
      : []),
    ...(principal && SETTINGS_MODULES.some((module) => allowed(principal, module, 'view'))
      ? [{ to: '/app/settings', label: 'Settings' }]
      : []),
  ] as const;

  const canCreateJobs = principal ? allowed(principal, 'jobs', 'edit') : false;
  const initials = (user.displayName ?? user.email ?? '?')
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="flex h-full flex-col">
      <header className="z-10 bg-slate-900 text-white">
        {/*
          `flex-wrap` and a smaller gap below `lg` (SCRUM-299). Adding
          the Menu button put this row three pixels over a 390px screen
          — measured, not guessed — and `desk-tables-mobile` caught the
          sideways scroll on pages that have nothing to do with the
          header. Wrapping means the next control added cannot do the
          same thing again.
        */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 lg:gap-6">
          <Link to="/app" className="font-semibold tracking-tight">
            Buildmate
          </Link>
          {/*
            The menu button, below `lg` only. A dropdown rather than a
            sidebar: it is the smaller change, and it leaves the page
            underneath where it was instead of pushing it sideways.
          */}
          <Button
            variant="onDark"
            size="sm"
            className="text-slate-300 hover:bg-slate-800 lg:hidden"
            aria-expanded={menuOpen}
            aria-controls="main-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            Menu
          </Button>
          <nav aria-label="Main" className="hidden gap-4 text-sm text-slate-300 lg:flex">
            {links.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                {...('search' in link ? { search: link.search } : {})}
                className="whitespace-nowrap hover:text-white"
                activeProps={{ className: 'whitespace-nowrap font-medium text-white' }}
                activeOptions={{ exact: link.to === '/app', includeSearch: false }}
              >
                {link.label}
              </Link>
            ))}
            {/*
              Modules that do not exist yet, shown greyed from `2xl` so
              somebody can see where the product is going without them
              taking room from the links that work.
            */}
            {NAV.map((item) => (
              <span
                key={item}
                aria-disabled="true"
                title="Arrives in a later phase"
                className="hidden cursor-default whitespace-nowrap text-slate-500 2xl:inline"
              >
                {item}
              </span>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <CompanySwitcher />
            {/* Reachable from every screen, which is the point: the
                question somebody wants to ask is about the page they are
                already on. */}
            <Button
              variant="onDark"
              size="sm"
              className="text-slate-300 hover:bg-slate-800"
              onClick={() => setAsking((open) => !open)}
            >
              Ask
            </Button>
            <NotificationBell />
            <span
              aria-hidden="true"
              className="grid h-8 w-8 place-items-center rounded-full bg-blue-600 text-xs font-semibold"
            >
              {initials}
            </span>
            <Button
              variant="onDark"
              size="sm"
              className="text-slate-300 hover:bg-slate-800"
              onClick={() => void ports.auth.signOut()}
            >
              Sign out
            </Button>
          </div>
        </div>
        {/*
          The menu itself: a panel under the bar rather than an overlay,
          so the page does not move and nothing has to trap focus.
          Closed by choosing something, by Escape, or by the button.
        */}
        {menuOpen && (
          <nav
            id="main-menu"
            aria-label="Main"
            className="flex flex-col gap-1 border-t border-slate-800 px-4 pb-3 lg:hidden"
          >
            {links.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                {...('search' in link ? { search: link.search } : {})}
                onClick={() => setMenuOpen(false)}
                className="rounded-md px-2 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white"
                activeProps={{
                  className: 'rounded-md bg-slate-800 px-2 py-2 text-sm font-medium text-white',
                }}
                activeOptions={{ exact: link.to === '/app', includeSearch: false }}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        )}
      </header>

      <div className="flex min-h-0 flex-1">
        <JobSidebar canCreate={canCreateJobs} onNewJob={() => setCreating(true)} />
        <main className="min-w-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
        {/* Beside the page rather than over it: the whole value is asking
            about what is on screen, and a pane that covered it would make
            people close it to check. Hidden below `lg`, where there is no
            room for both — on a phone it opens as the whole screen. */}
        {asking && (
          <div className="fixed inset-0 z-40 bg-white lg:static lg:z-auto lg:w-[26rem] lg:shrink-0">
            <AssistantPane onClose={() => setAsking(false)} />
          </div>
        )}
      </div>

      {/* Over everything, including the dialog above, and last in the tree
          so it paints on top. Mounted here rather than per page: a notice
          somebody can avoid by going straight to a URL is not a notice. */}
      <SafetySplash />

      <NewJobDialog
        open={creating}
        onClose={() => setCreating(false)}
        companyId={membership.companyId}
        companyTimeZone={company?.timeZone ?? 'America/Phoenix'}
        uid={user.uid}
        onCreated={() => setCreating(false)}
      />
    </div>
  );
}
